import { Box, Stack, Typography } from '@mui/material';
import { AttendanceCode, GroupMember, GroupUserType } from '@schooly/api';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { DATE_FORMAT, Genders, getLabelIdForOption } from '@schooly/constants';
import { LIST_ROW_COLUMNS } from '@schooly/constants/test-ids';
import { GridRowName, theme } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { PropsWithChildren, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import PersonCardBasic from '../../../../../components/common/PersonCard/PersonCardBasic';
import { GridCell, GridContainer } from '../../../../../components/uikit-components/Grid/Grid';
import { Table, TableCell, TableRow } from '../../../../../components/uikit-components/Table/Table';
import { GroupPreviewMembersListCardsWrapper } from './GroupPreviewMembersList.styled';
import { ViewType } from './ListViewSwitch';

const ATTENDANCE_CIRCLE_SIZE = 20;

interface GroupPreviewMembersListProps extends PropsWithChildren {
  groupMembers?: GroupMember[];
  userType: GroupUserType;
  headerTitle?: string;
  isDisabled?: boolean;
  tableView: ViewType;
  todayAttendance?: Record<string, AttendanceCode | undefined> | null;
}

export const GroupPreviewMembersList: React.FC<GroupPreviewMembersListProps> = ({
  groupMembers,
  userType,
  headerTitle = 'Title',
  isDisabled,
  tableView,
  todayAttendance,
}) => {
  const { formatMessage } = useIntl();

  const isCompressed = tableView === 'compressedView';
  const columns: TableCell[] = useMemo(() => {
    const baseColumn = {
      id: headerTitle,
      renderContent: () => {
        return <GridCell py={isCompressed ? 0.75 : 1.25}>{headerTitle}</GridCell>;
      },
    };
    if (userType === 'staff') return [baseColumn];

    const studentColumns: TableCell[] = [
      baseColumn,
      {
        id: 'DOB',
        renderContent: () => (
          <GridCell py={isCompressed ? 0.75 : 1}>
            <FormattedMessage id="date-of-birthday" />
          </GridCell>
        ),
      },
      {
        id: 'Gender',
        renderContent: () => (
          <GridCell py={isCompressed ? 0.75 : 1}>
            <FormattedMessage id="peopleDetail-Gender" />
          </GridCell>
        ),
      },
    ];

    if (todayAttendance !== undefined) {
      studentColumns.push({
        id: 'TodayAttendance',
        renderContent: () => (
          <GridCell py={isCompressed ? 0.75 : 1}>
            <FormattedMessage id="attendance-TodaysAttendance" />
          </GridCell>
        ),
      });
    }

    return studentColumns;
  }, [headerTitle, isCompressed, todayAttendance, userType]);

  const rows: TableRow[] = useMemo(() => {
    if (!groupMembers) {
      return [];
    }
    return groupMembers.map((member) => {
      const gender = Number.isInteger(member.user.gender)
        ? formatMessage({
            id: getLabelIdForOption({
              key: Genders[member.user.gender!],
              optionsKey: 'gender',
            }),
          })
        : '';

      const baseRow = {
        id: 'date',
        renderContent: () => (
          <GridCell noVerticalPadding>
            <GridRowName
              sx={{
                ...(!isCompressed && { pt: 0.75, pb: 0.9 }),
                maxWidth: 520,
              }}
            >
              <Box
                sx={{
                  '& .card-body': {
                    padding: '0 !important',
                  },
                  '.PersonCard__info': {
                    p: isCompressed ? 0 : undefined,
                  },
                }}
                minHeight={isCompressed ? 25 : undefined}
              >
                <PersonCardBasic
                  isListItem
                  isUsernameClickable
                  user={member.user}
                  userType={userType}
                  key={member.user.relation_id}
                  noAvatar={isCompressed}
                  usernameTestId={
                    userType === 'staff'
                      ? LIST_ROW_COLUMNS.staff_name
                      : LIST_ROW_COLUMNS.student_name
                  }
                />
              </Box>
            </GridRowName>
          </GridCell>
        ),
      };

      const code = todayAttendance?.[member.user.relation_id];

      const studentCells: TableCell[] = [
        baseRow,
        {
          id: 'DOB',
          renderContent: () => (
            <GridCell
              sx={{
                width: todayAttendance ? 120 : 100,
              }}
              noVerticalPadding
            >
              <Typography color="text.primary" data-test-id={LIST_ROW_COLUMNS.dob}>
                {member.user.date_of_birth
                  ? format(newDateTimezoneOffset(member.user.date_of_birth), DATE_FORMAT)
                  : ''}
              </Typography>
            </GridCell>
          ),
        },
        {
          id: 'Gender',
          renderContent: () => (
            <GridCell
              sx={{
                width: todayAttendance ? 120 : 90,
              }}
              noVerticalPadding
            >
              <Typography color="text.primary" data-test-id={LIST_ROW_COLUMNS.gender}>
                {gender}
              </Typography>
            </GridCell>
          ),
        },
      ];

      if (todayAttendance !== undefined) {
        studentCells.push({
          id: 'TodayAttendance',
          renderContent: () => (
            <GridCell sx={{ width: 220 }} noVerticalPadding>
              {todayAttendance === null ? (
                <Typography color="common.grey" data-test-id={LIST_ROW_COLUMNS.attendance_code}>
                  -
                </Typography>
              ) : code ? (
                <Stack direction="row" gap={0.75} alignItems="center">
                  <AttendanceCodeCircle code={code} size={ATTENDANCE_CIRCLE_SIZE} />
                  <Typography noWrap data-test-id={LIST_ROW_COLUMNS.attendance_code}>
                    {code.name}
                  </Typography>
                </Stack>
              ) : (
                <Typography color="common.grey" data-test-id={LIST_ROW_COLUMNS.attendance_code}>
                  {formatMessage({ id: 'attendance-NoStatus' })}
                </Typography>
              )}
            </GridCell>
          ),
        });
      }

      return {
        id: member.user.user_id ?? '',
        cells: userType === 'staff' ? [baseRow] : studentCells,
        props: () => ({
          sx: {
            '&:hover': {
              '.MuiTypography-root': {
                color: theme.palette.primary.main,
              },
            },
          },
        }),
      };
    });
  }, [formatMessage, groupMembers, isCompressed, todayAttendance, userType]);

  if (!groupMembers?.length) {
    return null;
  }

  return (
    <GridContainer
      sx={{
        mb: 0,
      }}
    >
      <Stack gap={2} data-scope>
        {!!groupMembers?.length && (
          <GroupPreviewMembersListCardsWrapper disabled={isDisabled}>
            <Table withDefaultHover rows={rows} columns={columns} />
          </GroupPreviewMembersListCardsWrapper>
        )}
      </Stack>
    </GridContainer>
  );
};
